import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Container, Grid, Typography } from '@mui/material';
import { BarChart, Bar, PieChart, Pie, RadarChart, Radar, PolarGrid, PolarAngleAxis, 
  PolarRadiusAxis, XAxis, YAxis, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import createInstance from '../../../api/axiosinstance';
import { useSelector } from 'react-redux';

export default function HRcandidate() {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const aiDisha = createInstance("DISHA");
  const organizationId = user?.organizationId
  const [dashboardData, setDashboardData] = useState({
    totalInterviews: 0,
    pipelineCandidates: 0,
    hirePercentage: 0,
    strongHirePercentage: 0,
    borderlinePercentage: 0,
    noHirePercentage: 0,
    pipelinePercentage: 0,
    averageScores: {}
  });
  const [averageScores, setAverageScores] = useState({
    technical_knowledge: 0,
    hands_on_skills: 0,
    problem_solving: 0,
    communication_skills: 0,
    culture_fit: 0
  });

  useEffect(()=>{
    Dashboard()
  },[])

  const Dashboard = async () => {
    try {
      const response = await aiDisha.get(`/hr_dashboard?organization_id=${organizationId}`);
      console.log("dashboard", response.data.average_graph_val);
      
      // Extract data from API response
      const data = response.data;
      const avgScores = data.average_graph_val[0]?.average_scores || {};
      
      // Set state with API data
      setDashboardData({
        totalInterviews: data['total interviews'] || 0,
        pipelineCandidates: data['pipeline_candidates'] || 0,
        hirePercentage: data['hire_percentage'] || 0,
        strongHirePercentage: data['strong_hire_percentage'] || 0,
        borderlinePercentage: data['borderline_percentage'] || 0,
        noHirePercentage: data['no_hire_percentage'] || 0,
        pipelinePercentage: data['pipeline_percentage'] || 0,
      });

     // Update average scores state
     setAverageScores({
       technical_knowledge: avgScores.technical_knowledge || 0,
       hands_on_skills: avgScores.hands_on_skills || 0,
       problem_solving: avgScores.problem_solving || 0,
       communication_skills: avgScores.communication_skills || 0,
       culture_fit: avgScores.culture_fit || 0
     });
    } catch (error) {
      console.log("error", error);
    }
  };


  useEffect(() => {
    console.log("Updated dashboard data:", dashboardData);
  }, [dashboardData]);
  // Data for Stats Cards
  const statsData = [
    { title: "Total Interviews", value: dashboardData.totalInterviews },
    { title: "Avg Score", value: "3.9 / 5" },
    { title: "Selection Percentage", value: dashboardData.hirePercentage },
    { title: "Pipeline Candidates", value: dashboardData.pipelineCandidates }
  ];

  // Data for Bar Chart
  const scoreData = [
    { name: 'Technical', score: averageScores.technical_knowledge },
    { name: 'Hands-on', score: averageScores.hands_on_skills },
    { name: 'Problem Solving', score: averageScores.problem_solving },
    { name: 'Communication', score: averageScores.communication_skills },
    { name: 'Culture Fit', score: averageScores.culture_fit }
  ];

  // Data for Pie Chart
  const decisionData = [
    { name: 'Strong Hire', value: dashboardData.strongHirePercentage, color: '#2e7d32' },
    { name: 'Hire', value: dashboardData.hirePercentage, color: '#1976d2' },
    { name: 'Borderline', value: dashboardData.borderlinePercentage, color: '#ed6c02' },
    { name: 'No Hire', value: dashboardData.noHirePercentage, color: '#d32f2f' },
    { name: 'Pipeline', value: dashboardData.pipelinePercentage, color: '#757575' }
  ];

  // Data for Radar Chart
  const skillData = [
    { subject: 'Coding', A: 4.0, fullMark: 5 },
    { subject: 'System Design', A: 3.5, fullMark: 5 },
    { subject: 'API Knowledge', A: 3.8, fullMark: 5 },
    { subject: 'CI/CD Tools', A: 3.2, fullMark: 5 },
    { subject: 'Communication', A: 4.1, fullMark: 5 }
  ];

  return (
    <Container sx={{ my: 5,color:"#033538" }}>
      <Typography variant="h4" component="h2" align="center" gutterBottom sx={{ mb: 4 }} >
        HR Interview Analytics Dashboard
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statsData.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {stat.title}
                </Typography>
                <Typography variant="h4" component="div" color="#033538">
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Score Distribution */}
      <Card elevation={1} sx={{ mb: 4, p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Average Score per Category
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={scoreData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis dataKey="name" />
                <YAxis domain={[0, 5]} />
                <Tooltip />
                <Legend />
                <Bar dataKey="score" fill="#1976d2" name="Avg Score (1-5)" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Decision Summary */}
      <Card elevation={1} sx={{ mb: 4, p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Candidate Decision Breakdown
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={decisionData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {decisionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} candidates`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* Skill Radar */}
      <Card elevation={1} sx={{ p: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="text.secondary">
            Skill Radar: Candidate Strengths
          </Typography>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart cx="50%" cy="50%" outerRadius="80%" data={skillData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <PolarRadiusAxis domain={[0, 5]} />
                <Radar
                  name="Average Candidate Skill"
                  dataKey="A"
                  stroke="#1976d2"
                  fill="#1976d2"
                  fillOpacity={0.3}
                />
                <Tooltip />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}
